import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { sleep } from '@/utils';

const TOKEN_KEY = 'hccc.token';
const CREDENTIALS_KEY = 'hccc.credentials';
const IDLE_TIMEOUT = 30 * 60 * 1000; // 30 minutes idle timeout
let lastActivity = Date.now();

// Track user activity to reset idle timer
function trackActivity(): void {
  lastActivity = Date.now();
}

// Set up activity tracking
if (typeof window !== 'undefined') {
  ['mousedown', 'keydown', 'scroll', 'touchstart'].forEach((event) => {
    window.addEventListener(event, trackActivity);
  });
}

// Check if JWT token is expired by decoding it
function isTokenExpired(token: string): boolean {
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    const exp = decoded.exp;
    return exp * 1000 < Date.now();
  } catch {
    return true; // If we can't decode, assume expired
  }
}

export function getStoredToken(): string | null {
  // Check if token has expired due to idle time
  if (Date.now() - lastActivity > IDLE_TIMEOUT) {
    setStoredToken(null);
    return null;
  }

  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${TOKEN_KEY}=`);
  if (parts.length === 2) {
    const token = parts.pop()?.split(';').shift() ?? null;
    // Check if token is expired
    if (token && isTokenExpired(token)) {
      setStoredToken(null);
      setStoredCredentials(null);
      return null;
    }
    return token;
  }
  return null;
}

export function setStoredToken(token: string | null): void {
  if (token) {
    // Set cookie with 30 day expiry; only use secure flag on HTTPS (not localhost dev)
    const expires = new Date();
    expires.setDate(expires.getDate() + 30);
    const secureFlag = location.protocol === 'https:' ? 'secure;' : '';
    document.cookie = `${TOKEN_KEY}=${token}; expires=${expires.toUTCString()}; path=/; ${secureFlag}samesite=strict`;
    lastActivity = Date.now();
  } else {
    // Remove cookie
    const clearSecure = location.protocol === 'https:' ? 'secure;' : '';
    document.cookie = `${TOKEN_KEY}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; ${clearSecure}samesite=strict`;
    // Also clear all cookies for the domain
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
      document.cookie = `${name.trim()}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; ${clearSecure}samesite=strict`;
    }
  }
}

export interface StoredCredentials {
  userID: string;
  pin: string;
}

export function getStoredCredentials(): StoredCredentials | null {
  try {
    const stored = localStorage.getItem(CREDENTIALS_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore parsing errors
  }
  return null;
}

export function setStoredCredentials(credentials: StoredCredentials | null): void {
  if (credentials) {
    localStorage.setItem(CREDENTIALS_KEY, JSON.stringify(credentials));
  } else {
    localStorage.removeItem(CREDENTIALS_KEY);
  }
}

interface RetryableConfig extends InternalAxiosRequestConfig {
  _retryCount?: number;
  _isTokenRefresh?: boolean;
}

const MAX_RETRIES = 2;
const RETRYABLE_STATUSES = new Set([502, 503, 504]);

export function attachInterceptors(client: AxiosInstance): void {
  client.interceptors.request.use((config) => {
    const token = getStoredToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const config = error.config as RetryableConfig | undefined;
      const status = error.response?.status;

      // Handle 401 unauthorized - clear session and redirect to login
      if (status === 401 && config && !config._isTokenRefresh) {
        setStoredToken(null);
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      }

      // Handle retryable server errors
      if (
        config &&
        config.method?.toLowerCase() === 'get' &&
        (status === undefined || RETRYABLE_STATUSES.has(status)) &&
        (config._retryCount ?? 0) < MAX_RETRIES
      ) {
        config._retryCount = (config._retryCount ?? 0) + 1;
        await sleep(300 * config._retryCount);
        return client.request(config);
      }

      return Promise.reject(error);
    },
  );
}
